import { type BudgetMixBreakdownSpendDatum } from "@north-beam/nb-common";

type GenericAggregator<T> = (a: T, b: T) => T;
type GenericValueAccessor<T, R> = (data: T) => R;
type GenericKeyAccessor<T> = (data: T) => string;

export const aggregateBudgetMixSpendByPrimaryBreakdown = (
  data: BudgetMixBreakdownSpendDatum[],
) => {
  const sum = (a: number, b: number) => a + b;
  return aggregateReduce(
    data,
    (datum) => datum.breakdowns[0].value,
    (datum) => datum.spend,
    sum,
    0,
  );
};

export function aggregateReduce<T extends Record<string, any>, V>(
  data: T[],
  groupByAccessor: GenericKeyAccessor<T>,
  aggregateAccessor: GenericValueAccessor<T, V>,
  combine: GenericAggregator<V>,
  initialValue: V,
): Record<string, V> {
  const aggregatedData = aggregate(data, groupByAccessor);
  const reducedData = {} as Record<string, V>;
  for (const groupByValue in aggregatedData) {
    reducedData[groupByValue] = aggregatedData[groupByValue].reduce(
      (agg, val) => combine(agg, aggregateAccessor(val)),
      initialValue,
    );
  }
  return reducedData;
}

export function aggregate<T extends Record<string, any>>(
  data: T[],
  groupBy: GenericKeyAccessor<T>,
): Record<string, T[]> {
  const reducer = (agg: Record<string, T[]>, val: T) => {
    const valueOfConcern = groupBy(val);
    if (agg[valueOfConcern]) {
      agg[valueOfConcern].push(val);
    } else {
      agg[valueOfConcern] = [val];
    }

    return agg;
  };
  return data.reduce(reducer, {} as Record<string, T[]>);
}

export function reverseMap<
  K extends string | number | symbol,
  V extends string | number | symbol,
>(map: Record<K, V>): Map<V, K[]> {
  const reversed: Map<V, K[]> = new Map();
  for (const key of Object.keys(map)) {
    const val = map[key as K];
    const existingKeyArray = reversed.get(val);
    if (existingKeyArray) {
      const appendedKeyArray: K[] = [...existingKeyArray, key as K];
      reversed.set(val, appendedKeyArray);
    } else {
      reversed.set(val, [key as K]);
    }
  }

  return reversed;
}
